/**
 * Attribution capture — click IDs + UTMs (the tracking moat).
 *
 * Captures gclid/gbraid/wbraid/fbclid/ttclid/msclkid and utm_* from:
 *  1. this page's own URL (direct visits, embeds that forward params)
 *  2. document.referrer (iframe embeds: the click ID lives on the PARENT page
 *     URL, and the parent URL is the iframe's referrer on first load)
 *
 * Persists first/last touch in localStorage (90 days) so a visitor who clicks
 * an ad Monday and submits Thursday still carries their gclid.
 */

const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid'];
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const STORE_KEY = 'sf_attr_v1';
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

function paramsFromUrl(url) {
  const out = {};
  try {
    const u = new URL(url);
    for (const k of [...CLICK_ID_KEYS, ...UTM_KEYS]) {
      const v = u.searchParams.get(k);
      if (v) out[k] = v.slice(0, 256);
    }
  } catch (e) {
    /* invalid/empty URL — ignore */
  }
  return out;
}

function loadStore() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const store = JSON.parse(raw);
    // Expire stale attribution so a years-old gclid never misattributes
    if (store?.lastTouchAt && Date.now() - new Date(store.lastTouchAt).getTime() > TTL_MS) {
      localStorage.removeItem(STORE_KEY);
      return null;
    }
    return store;
  } catch (e) {
    return null;
  }
}

function saveStore(store) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch (e) {
    /* storage unavailable (private mode etc.) — session-only capture still works */
  }
}

/**
 * Capture any attribution present right now and return the merged best-known
 * attribution object to attach to submissions / chat messages.
 * Safe to call multiple times; call once on page load and again at submit.
 *
 * @param {Object} [extraParams] — attribution forwarded by embed.js via postMessage
 *   (the parent page URL, which is the true source for adopted-embed ad params).
 *   Allowed keys: CLICK_ID_KEYS + UTM_KEYS. Takes precedence over referrer and
 *   own URL so the host landing-page gclid fills gaps the iframe URL can't carry.
 *   First-touch is still preserved: extraParams only flow through the same
 *   saveStore / firstTouchAt guard as normally-found params.
 */
export function collectAttribution(extraParams) {
  // Sanitize extraParams: only known keys, values capped to 256 chars.
  const extra = {};
  if (extraParams && typeof extraParams === 'object') {
    for (const k of [...CLICK_ID_KEYS, ...UTM_KEYS]) {
      const v = extraParams[k];
      if (v) extra[k] = String(v).slice(0, 256);
    }
  }

  const found = {
    ...paramsFromUrl(document.referrer || ''),
    // Own URL wins over referrer for the same key (fresher)
    ...paramsFromUrl(window.location.href),
    // Forwarded parent-page params win last — the host landing page is the
    // canonical source of ad click IDs for adopted iframes.
    ...extra,
  };

  let store = loadStore();
  const now = new Date().toISOString();

  if (Object.keys(found).length > 0) {
    store = {
      // Per-key last-touch wins (ad platforms attribute to the latest click)
      params: { ...((store && store.params) || {}), ...found },
      firstTouchAt: (store && store.firstTouchAt) || now,
      lastTouchAt: now,
      landingUrl:
        (store && store.landingUrl) ||
        (document.referrer && Object.keys(paramsFromUrl(document.referrer)).length > 0
          ? document.referrer
          : window.location.href),
    };
    saveStore(store);
  }

  const params = (store && store.params) || found;
  const clickIds = {};
  const utms = {};
  for (const k of CLICK_ID_KEYS) if (params[k]) clickIds[k] = params[k];
  for (const k of UTM_KEYS) if (params[k]) utms[k] = params[k];

  if (Object.keys(clickIds).length === 0 && Object.keys(utms).length === 0) {
    return null; // nothing to report — keep payloads clean
  }

  return {
    clickIds,
    utms,
    landingUrl: (store && store.landingUrl) || window.location.href,
    referrer: document.referrer || null,
    firstTouchAt: (store && store.firstTouchAt) || now,
    lastTouchAt: (store && store.lastTouchAt) || now,
  };
}

export { CLICK_ID_KEYS, UTM_KEYS };
